// ABOUTME: Dialog listing what the cascade delete of one node of a bulk delete removes, hierarchy by hierarchy.
// ABOUTME: Reads the paged CascadeDeletePreview query and renders it with the node-delete module's table.

import { Box, Dialog, Stack, Typography } from '@ds/coolshop';
import {
    CascadeBlockersAlert,
    DeleteTableRow,
    LinkedNodesDeleteTable,
    useCascadeDeletePreviewQuery,
} from '@src/_new/modules/node-delete';
import { HierarchyList } from '@src/modules/hierarchy/LinkedByModal/components/HierarchyList';
import { TwoColumnLayout } from '@src/modules/hierarchy/LinkedByModal/components/TwoColumnLayout';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const DEFAULT_PAGE_SIZE = 10;

type CascadeDeletePreviewDialogProps = {
    hierarchyId: string;
    nodeId: string;
    nodeCode: string;
    onClose: () => void;
};

export function CascadeDeletePreviewDialog({
    hierarchyId,
    nodeId,
    nodeCode,
    onClose,
}: CascadeDeletePreviewDialogProps) {
    const { formatMessage } = useIntl();
    const [selectedHierarchyId, setSelectedHierarchyId] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });

    const selectHierarchy = (id: string) => {
        setSelectedHierarchyId(id);
        setPagination(current => ({ ...current, pageIndex: 0 }));
    };

    const query = useCascadeDeletePreviewQuery({
        variables: {
            hierarchyId,
            nodeId,
            linkingHierarchyId: selectedHierarchyId,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
        },
        fetchPolicy: 'network-only',
    });
    // Apollo drops `data` while the next page is in flight: keep the previous page meanwhile.
    const preview =
        query.data?.getCascadeDeletePreview ?? query.previousData?.getCascadeDeletePreview;

    const hierarchies = useMemo(
        () =>
            (preview?.groups ?? []).map(group => ({
                hierarchyId: group.hierarchyId,
                hierarchyCode: group.hierarchyCode,
                objectSchemaCode: group.objectSchemaCode,
                totalCount: group.totalCount,
            })),
        [preview],
    );

    useEffect(() => {
        if (hierarchies.length === 0) return;
        if (!hierarchies.some(h => h.hierarchyId === selectedHierarchyId)) {
            selectHierarchy(hierarchies[0].hierarchyId);
        }
    }, [hierarchies, selectedHierarchyId]);

    const selectedGroup = preview?.groups.find(g => g.hierarchyId === selectedHierarchyId);
    const rows: DeleteTableRow[] = useMemo(
        () =>
            (selectedGroup?.items ?? []).map(item => ({
                id: item.nodeId,
                code: item.code,
                listName: item.listName,
                parent: item.parent,
                detail:
                    item.reason === 'links'
                        ? formatMessage(
                              { id: 'node-delete.reason.links', defaultMessage: 'links {via}' },
                              { via: item.viaCode ?? '' },
                          )
                        : formatMessage(
                              {
                                  id: 'node-delete.reason.descendant',
                                  defaultMessage: 'deleted with {via}',
                              },
                              { via: item.viaCode ?? '' },
                          ),
            })),
        [selectedGroup, formatMessage],
    );

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            disablePaddings
            header={{
                title: formatMessage(
                    {
                        id: 'bulkChange.cascadePreview.title',
                        defaultMessage: 'Deleted with {code}',
                    },
                    { code: nodeCode },
                ),
                onClose,
            }}
        >
            <Box px={3} pt={2} pb={2.5}>
                <Stack gap={1.5} height={520}>
                    <Typography variant="body2" color="textSecondary">
                        {formatMessage(
                            {
                                id: 'bulkChange.cascadePreview.description',
                                defaultMessage:
                                    'Deleting {code} also deletes these objects: its descendants and the objects linking to them, in every hierarchy.',
                            },
                            { code: nodeCode },
                        )}
                    </Typography>
                    {/* The preview reads the links as they are now, which may differ from the scan. */}
                    <CascadeBlockersAlert
                        capExceeded={preview?.capExceeded === true}
                        lockedNodes={preview?.lockedNodes ?? []}
                    />
                    <Box flex={1} minHeight={0}>
                        <TwoColumnLayout
                            leftColumn={
                                <HierarchyList
                                    hierarchies={hierarchies}
                                    selectedHierarchy={
                                        hierarchies.find(
                                            h => h.hierarchyId === selectedHierarchyId,
                                        ) ?? null
                                    }
                                    onHierarchySelect={h => selectHierarchy(h.hierarchyId)}
                                    loading={query.loading}
                                />
                            }
                            rightColumn={
                                <LinkedNodesDeleteTable
                                    rows={rows}
                                    totalCount={selectedGroup?.totalCount ?? 0}
                                    loading={query.loading}
                                    page={pagination.pageIndex}
                                    pageSize={pagination.pageSize}
                                    onPageChange={(pageIndex, pageSize) =>
                                        setPagination({ pageIndex, pageSize })
                                    }
                                    detailHeader={formatMessage({
                                        id: 'node-delete.table.reason',
                                        defaultMessage: 'Reason',
                                    })}
                                    emptyMessage={formatMessage({
                                        id: 'node-delete.table.empty',
                                        defaultMessage: 'No nodes involved.',
                                    })}
                                />
                            }
                        />
                    </Box>
                </Stack>
            </Box>
        </Dialog>
    );
}
